/** KPI metric card for the Waste Management Dashboard - shows a value, a small
 * comparison-to-target/prior-period line and a status-coloured left border, with the status
 * also conveyed via a small text-labelled dot so it never relies on colour alone. */

import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { KpiCardData } from '../types';
import { statusToColor } from './StatusChip';

const STATUS_HEX: Record<KpiCardData['status'], string> = {
  good: '#12B76A',
  warning: '#F79009',
  critical: '#F04438',
};

interface KpiCardProps {
  data: KpiCardData;
}

const KpiCard = ({ data }: KpiCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const STATUS_LABEL: Record<KpiCardData['status'], string> = {
    good: t('wasteDashboardStatusOnTrack', 'On track'),
    warning: t('wasteDashboardStatusWatch', 'Watch'),
    critical: t('wasteDashboardStatusActionNeeded', 'Action needed'),
  };
  return (
    <Box sx={{
      border: '1px solid',
      borderColor: theme.palette.custom.gray[300],
      borderLeft: '4px solid',
      borderLeftColor: STATUS_HEX[data.status],
      borderRadius: '10px',
      p: 3,
      height: '100%',
    }}>
      <Box display="flex"
        alignItems="center"
        justifyContent="space-between">
        <Typography variant="textXS"
          color="text.secondary">
          {data.label}
        </Typography>
        <Typography variant="textXS"
          color={`${statusToColor(data.status)}.main`}
          fontWeight={600}>
          {STATUS_LABEL[data.status]}
        </Typography>
      </Box>
      <Typography variant="textLG"
        fontWeight={600}
        sx={{ mt: 0.5 }}>
        {data.value}
      </Typography>
      <Typography variant="textXS"
        color="text.secondary"
        sx={{ mt: 0.5, display: 'block' }}>
        {data.comparisonLabel}
      </Typography>
    </Box>
  );
};

export default KpiCard;
